import { useState } from "react";
import { useNavigate } from "react-router";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useAuth } from "@/modules/auth";
import Input from "@/components/ui/input";
import IconLogout from "@/components/icons/icon-logout";
import Button from "@/components/ui/button";

const profileSchema = z.object({
	first_name: z.string().min(1, "First name is required"),
	last_name: z.string().min(1, "Last name is required"),
});

type ProfileFormData = z.infer<typeof profileSchema>;

export default function CompleteProfilePage() {
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const navigate = useNavigate();
	const { updateProfile, logout } = useAuth();
	const {
		register,
		handleSubmit,
		formState: { errors },
	} = useForm<ProfileFormData>({
		resolver: zodResolver(profileSchema),
		defaultValues: {
			first_name: "",
			last_name: "",
		},
	});

	const handleLogout = async () => {
		try {
			await logout();
			navigate("/login", { replace: true });
		} catch (error) {
			console.error("Logout failed:", error);
		}
	};

	const onSubmit = async (data: ProfileFormData) => {
		setIsLoading(true);
		setError(null);

		try {
			// Update profile using auth store
			await updateProfile(data);
		} catch (err) {
			setError(err instanceof Error ? err.message : "An error occurred");
		} finally {
			setIsLoading(false);
		}
	};

	return (
		<div className="w-full sm:w-lg flex-col p-0 sm:p-8">
			<div className="flex items-center justify-between mb-6 sm:mb-8">
				<img className="w-12 sm:w-16" src="/images/sayaboutus-logomark-light.svg" alt="Say About Us" />
				<button type="button" onClick={handleLogout} className="flex items-center gap-1.5 text-sm text-tertiary hover:text-primary cursor-pointer">
					<IconLogout />
					Logout
				</button>
			</div>
			<h1 className="text-lg sm:text-xl font-semibold text-primary -leading-[1%]">Almost there!</h1>
			<p className="text-sm mt-2 sm:mt-3 text-tertiary font-medium mb-4 sm:mb-6">Tell us your name so we know what to call you.</p>
			<form onSubmit={handleSubmit(onSubmit)}>
				<div className="space-y-6">
					<Input label="First Name" type="text" disabled={isLoading} error={errors.first_name?.message} {...register("first_name")} />
					<Input label="Last Name" type="text" disabled={isLoading} error={errors.last_name?.message} {...register("last_name")} />
					{error && <p className="text-xs text-red-500">{error}</p>}
					<div className="pt-2">
						<Button type="submit" disabled={isLoading} className="w-full">
							{isLoading ? "Saving..." : "Continue"}
						</Button>
					</div>
				</div>
			</form>
		</div>
	);
}
